import { AlertCircle, CheckCircle2, Loader2, Mic, Volume2 } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '~/components/ui/button';

interface DeviceTestProps {
  onConfirm: () => void;
}

type MicStatus = 'idle' | 'requesting' | 'active' | 'denied' | 'error';

export function DeviceTest({ onConfirm }: DeviceTestProps): React.ReactNode {
  const { t } = useTranslation();
  const [micStatus, setMicStatus] = useState<MicStatus>('idle');
  const [micLevel, setMicLevel] = useState(0);
  const [speakerTested, setSpeakerTested] = useState(false);
  const streamRef = useRef<MediaStream | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const animationRef = useRef<number>(0);

  const requestMicrophone = useCallback(async () => {
    setMicStatus('requesting');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      setMicStatus('active');

      const audioContext = new AudioContext();
      audioContextRef.current = audioContext;
      const source = audioContext.createMediaStreamSource(stream);
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 256;
      source.connect(analyser);

      const dataArray = new Uint8Array(analyser.frequencyBinCount);

      function updateLevel(): void {
        analyser.getByteFrequencyData(dataArray);
        const average = dataArray.reduce((sum, val) => sum + val, 0) / dataArray.length;
        setMicLevel(Math.min(average / 128, 1));
        animationRef.current = requestAnimationFrame(updateLevel);
      }

      updateLevel();
    } catch {
      setMicStatus('denied');
    }
  }, []);

  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => {
          track.stop();
        });
      }
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      if (audioContextRef.current) {
        void audioContextRef.current.close();
      }
    };
  }, []);

  const playSpeakerTest = useCallback(() => {
    const audioContext = new AudioContext();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);
    oscillator.frequency.value = 440;
    gainNode.gain.value = 0.3;
    oscillator.start();
    gainNode.gain.exponentialRampToValueAtTime(0.001, audioContext.currentTime + 0.5);
    oscillator.stop(audioContext.currentTime + 0.5);
    setSpeakerTested(true);
  }, []);

  const canProceed = micStatus === 'active' && speakerTested;

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="w-full max-w-sm">
        <div className="rounded-2xl border border-border bg-card px-8 py-10 shadow-sm">
          <div className="mb-6 flex flex-col items-center gap-3">
            <div className="text-center">
              <h1 className="text-xl">{t('screening.deviceTest.title')}</h1>
              <p className="mt-1 text-sm text-muted-foreground">
                {t('screening.deviceTest.description')}
              </p>
            </div>
          </div>

          <div className="flex flex-col gap-4">
            {/* Microphone test */}
            <div className="rounded-lg border border-border p-4">
              <div className="mb-3 flex items-center gap-2">
                <Mic className="size-4 text-muted-foreground" />
                <span className="text-sm font-medium">{t('screening.deviceTest.microphone')}</span>
                {micStatus === 'active' && (
                  <CheckCircle2 className="ml-auto size-4 text-green-500" />
                )}
                {micStatus === 'denied' && (
                  <AlertCircle className="ml-auto size-4 text-destructive" />
                )}
              </div>

              {micStatus === 'idle' && (
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full"
                  onClick={() => {
                    void requestMicrophone();
                  }}
                >
                  {t('screening.deviceTest.testMicrophone')}
                </Button>
              )}

              {micStatus === 'requesting' && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="size-4 animate-spin" />
                  {t('screening.deviceTest.requestingPermission')}
                </div>
              )}

              {micStatus === 'active' && (
                <div className="h-2 overflow-hidden rounded-full bg-muted">
                  <div
                    className="h-full rounded-full bg-green-500 transition-all duration-75"
                    style={{ width: `${String(Math.max(micLevel * 100, 2))}%` }}
                  />
                </div>
              )}

              {micStatus === 'denied' && (
                <p className="text-xs text-destructive">
                  {t('screening.deviceTest.microphoneDenied')}
                </p>
              )}
            </div>

            {/* Speaker test */}
            <div className="rounded-lg border border-border p-4">
              <div className="mb-3 flex items-center gap-2">
                <Volume2 className="size-4 text-muted-foreground" />
                <span className="text-sm font-medium">{t('screening.deviceTest.speakers')}</span>
                {speakerTested && <CheckCircle2 className="ml-auto size-4 text-green-500" />}
              </div>

              <Button variant="outline" size="sm" className="w-full" onClick={playSpeakerTest}>
                {speakerTested
                  ? t('screening.deviceTest.playAgain')
                  : t('screening.deviceTest.testSpeakers')}
              </Button>
            </div>

            {/* Confirm button */}
            <Button className="mt-2 w-full" disabled={!canProceed} onClick={onConfirm}>
              {t('screening.deviceTest.confirm')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
